import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { message, Button, theme } from 'antd';

import ShopifyProductsFilter from './components/ShopifyProductsFilter';
import ShopifyProductsList from './components/ShopifyProductsList';
import SyncShopifyProductsModal from './components/SyncShopifyProductsModal';
import { fetchShopifyProducts, applyFilters, syncShopifyProducts } from './actions';
import './ShopifyProducts.css';

const ShopifyProducts = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        token: { colorBgContainer, borderRadiusLG },
    } = theme.useToken();
    const {
        shopifyProducts,
        loading,
        pagination,
        filters: { searchText, selectedStatus, selectedShopifyStore }
    } = useSelector((state) => state.shopifyProducts);

    const [syncModalVisible, setSyncModalVisible] = useState(false);
    const [syncLoading, setSyncLoading] = useState(false);

    useEffect(() => {
        // Fetch first page of data when component mounts
        dispatch(fetchShopifyProducts(1, pagination.pageSize));
    }, [dispatch, pagination.pageSize]);

    const handleSearch = (value) => {
        dispatch(applyFilters(value, selectedStatus, selectedShopifyStore));
    };

    const handleStatusChange = (value) => {
        dispatch(applyFilters(searchText, value, selectedShopifyStore));
    };

    const handleShopifyStoreChange = (value) => {
        dispatch(applyFilters(searchText, selectedStatus, value));
    };

    const handleSyncClick = () => {
        setSyncModalVisible(true);
    };

    const handleSyncOk = async (syncData) => {
        try {
            setSyncLoading(true);

            if (!syncData.selectedIds || syncData.selectedIds.length === 0) {
                message.error('Vui lòng chọn ít nhất một Shopify Store');
                return;
            }

            const requestData = {
                shopifyStoreIds: syncData.selectedIds,
                syncType: syncData.syncType || 'PRODUCTS'
            };

            // Thêm date filters nếu có
            if (syncData.createdAfter && syncData.createdBefore) {
                requestData.createdAfter = syncData.createdAfter;
                requestData.createdBefore = syncData.createdBefore;
            } else if (syncData.updatedAfter && syncData.updatedBefore) {
                requestData.updatedAfter = syncData.updatedAfter;
                requestData.updatedBefore = syncData.updatedBefore;
            }

            const result = await dispatch(syncShopifyProducts(requestData));

            if (result.success) {
                message.success(result.message);
                setSyncModalVisible(false);
            } else {
                message.error(result.message);
            }
        } catch (error) {
            console.error('Error syncing Shopify products:', error);
            message.error('Có lỗi xảy ra khi đồng bộ products từ Shopify');
        } finally {
            setSyncLoading(false);
        }
    };

    const handleSyncCancel = () => {
        setSyncModalVisible(false);
    };

    // Memoize style objects to prevent re-creation
    const mainContainerStyle = useMemo(() => ({
        margin: '0px 10px 10px 10px',
        padding: '10px 16px 16px 16px',
        background: colorBgContainer,
        borderRadius: borderRadiusLG,
        minHeight: 280,
    }), [colorBgContainer, borderRadiusLG]);

    return (
        <div>
            <div
                style={{
                    margin: '10px 16px 0px 16px',
                }}
            >
                <ShopifyProductsFilter
                    onSearch={handleSearch}
                    onStatusChange={handleStatusChange}
                    onShopifyStoreChange={handleShopifyStoreChange}
                    searchText={searchText}
                    selectedStatus={selectedStatus}
                    selectedShopifyStore={selectedShopifyStore}
                    loading={loading}
                />
            </div>
            <div style={mainContainerStyle}>
                <ShopifyProductsList
                    shopifyProducts={shopifyProducts}
                    isLoading={loading}
                    pagination={pagination}
                    onSyncClick={handleSyncClick}
                />
            </div>
            <SyncShopifyProductsModal
                visible={syncModalVisible}
                onOk={handleSyncOk}
                onCancel={handleSyncCancel}
                loading={syncLoading}
            />
        </div>
    );
};

export default ShopifyProducts;
